import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createCompany,
  fetchMe,
  grantPlatformOperator,
  listPlatformCompanies,
  listPlatformOperators,
  logout,
  revokePlatformOperator,
  type PlatformOperator,
} from "@/shared/api/auth";

type CompanyRow = {
  id: string;
  slug: string;
  name: string;
  status: string;
  currencyCode: string;
  taxPriceMode: string;
  discountSoftCapPct: number;
};

export function PlatformPage() {
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [operators, setOperators] = useState<PlatformOperator[]>([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [operatorEmail, setOperatorEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  /** After create — Open company CTA (plan/18 first-client path) */
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const [co, op] = await Promise.all([
      listPlatformCompanies(),
      listPlatformOperators(),
    ]);
    setCompanies(co.companies);
    setOperators(op.operators);
  }

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then(async (me) => {
        if (cancelled) return;
        if (!me.user.isSuperadmin) {
          navigate("/select-company", { replace: true });
          return;
        }
        await reload();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "";
        if (msg === "Not signed in") {
          navigate("/login", { replace: true });
          return;
        }
        setError(msg || "Failed to load platform");
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const created = await createCompany({
        name,
        slug,
        ...(ownerEmail.trim() ? { ownerEmail: ownerEmail.trim() } : {}),
      });
      setName("");
      setSlug("");
      setOwnerEmail("");
      await reload();
      // Stay on Platform — owner finishes details in org Settings (plan/18).
      setCreatedSlug(created.company.slug);
      setStatus(
        `Created ${created.company.name}. Open the company when you’re ready — defaults cover the first cashiers and path.`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  async function onGrantOperator(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await grantPlatformOperator(operatorEmail.trim());
      setOperatorEmail("");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Grant failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRevokeOperator(op: PlatformOperator) {
    if (op.seed) return;
    const label = op.email ?? op.displayName ?? op.id;
    const ok = await confirmUi({
      title: `Revoke ${label}?`,
      description:
        "They will lose platform access immediately and can no longer onboard companies.",
      confirmLabel: "Revoke",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await revokePlatformOperator(op.id);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Revoke failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="animate-[softRise_400ms_ease-out]">
      <PageHeader
        title="Platform"
        description="Add a company (name, slug, first owner). Users, branches, tax, and terminals stay in company Settings — this screen stays separate from org UI."
      />

      <form className="mt-8 space-y-3" onSubmit={(e) => void onCreate(e)}>
        <div>
          <Label htmlFor="platform-company-name">Company name</Label>
          <Input
            id="platform-company-name"
            className="mt-1"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug || slug === slugify(name)) {
                setSlug(slugify(e.target.value));
              }
            }}
            required
          />
        </div>
        <div>
          <Label htmlFor="platform-company-slug">Slug</Label>
          <Input
            id="platform-company-slug"
            className="mt-1 font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            required
          />
        </div>
        <div>
          <Label htmlFor="platform-owner-email">
            First owner Google email (optional)
          </Label>
          <Input
            id="platform-owner-email"
            type="email"
            className="mt-1"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="owner@shop.ph — blank = you"
          />
        </div>
        <Button type="submit" disabled={busy} className="w-full">
          Add company
        </Button>
      </form>

      {status && (
        <Alert variant="default" className="mt-4 text-[var(--color-ok)]">
          {status}
          {createdSlug ? (
            <div className="mt-3">
              <Button asChild size="sm">
                <Link to={`/c/${createdSlug}`} className="no-underline">
                  Open {createdSlug}
                </Link>
              </Button>
            </div>
          ) : null}
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <h2 className="mt-10 m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-fg)]">
        Companies
      </h2>
      {companies.length === 0 ? (
        <div className="mt-3">
          <EmptyState
            title="None yet"
            description="Create the first company above to start onboarding."
          />
        </div>
      ) : (
        <ul className="mt-3 m-0 list-none space-y-2 p-0">
          {companies.map((co) => (
            <li key={co.id}>
              <Button
                asChild
                variant="outline"
                className="h-auto w-full justify-between gap-3 py-3"
              >
                <Link to={`/c/${co.slug}`} className="no-underline">
                  <span className="font-medium">{co.name}</span>
                  <span className="font-mono text-xs text-[var(--color-muted)]">
                    {co.slug} · {co.taxPriceMode} · cap {co.discountSoftCapPct}%
                  </span>
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      )}

      <h2 className="mt-10 m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-fg)]">
        Superadmins
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-[var(--color-muted)]">
        Who may onboard companies here. Grant only from this page — never via
        org roles.
      </p>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => void onGrantOperator(e)}
      >
        <Input
          type="email"
          className="min-w-0 flex-1"
          value={operatorEmail}
          onChange={(e) => setOperatorEmail(e.target.value)}
          placeholder="colleague@…"
          required
        />
        <Button type="submit" disabled={busy} variant="outline">
          Grant
        </Button>
      </form>
      <ul className="mt-3 m-0 list-none space-y-2 p-0">
        {operators.map((op) => (
          <li
            key={op.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]/70 px-3 py-2.5 text-sm"
          >
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="truncate">
                {op.email ?? op.displayName ?? op.id}
              </span>
              {op.seed ? <Badge variant="outline">seed</Badge> : null}
            </span>
            {!op.seed && (
              <button
                type="button"
                className="shrink-0 border-0 bg-transparent p-0 text-xs text-[var(--color-muted)] underline-offset-2 hover:underline"
                disabled={busy}
                onClick={() => void onRevokeOperator(op)}
              >
                Revoke
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-10 flex flex-wrap gap-4 text-sm">
        <Link
          to="/select-company"
          className="no-underline hover:underline"
        >
          Exit to companies
        </Link>
        <button
          type="button"
          className="border-0 bg-transparent p-0 text-[var(--color-muted)] underline-offset-2 hover:underline"
          onClick={() => void logout().then(() => navigate("/login"))}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
